import { DemoNetworkBanner } from "@/components/layout/DemoNetworkBanner";
import { networkLabel } from "@/lib/network";
import { MOBILE_TABS, MORE_LINKS, ROUTES } from "@/lib/routes";
import { cn } from "@/lib/utils";
import { Gavel, MoreHorizontal, Coins, Vault } from "lucide-react";
import { NavLink, useLocation } from "react-router-dom";

const ICONS = {
	Borrow: Coins,
	Vaults: Vault,
	Liquidate: Gavel,
	More: MoreHorizontal,
} as const;

const PRIMARY_PREFIXES = [ROUTES.borrow, ROUTES.vaults, ROUTES.liquidation];

/** Paths that should light up the bottom More tab (aligned with MORE_LINKS). */
const MORE_PATH_PREFIXES = [ROUTES.more, ...MORE_LINKS.map((l) => l.to)];

function isPrimaryPath(pathname: string): boolean {
	return PRIMARY_PREFIXES.some(
		(p) => pathname === p || pathname.startsWith(`${p}/`),
	);
}

/**
 * Mobile bottom chrome: primary tabs, then optional soft Play/local strip under them.
 */
export function BottomTabNav() {
	const { pathname } = useLocation();
	const net = networkLabel();
	const showSandbox = net === "play" || net === "local";

	return (
		<div className="fixed inset-x-0 bottom-0 z-50 md:hidden">
			<nav
				className="border-t border-border bg-card/95 backdrop-blur-xl"
				aria-label="Primary"
			>
				<ul className="mx-auto flex max-w-lg items-stretch justify-around px-1">
					{MOBILE_TABS.map((tab) => {
						const Icon =
							ICONS[tab.label as keyof typeof ICONS] ?? MoreHorizontal;
						let isActive =
							tab.match === "exact"
								? pathname === tab.to
								: pathname === tab.to || pathname.startsWith(`${tab.to}/`);
						if (tab.to === ROUTES.more) {
							isActive =
								!isPrimaryPath(pathname) &&
								MORE_PATH_PREFIXES.some(
									(p) => pathname === p || pathname.startsWith(`${p}/`),
								);
						}
						return (
							<li key={tab.to} className="flex-1">
								<NavLink
									to={tab.to}
									className={cn(
										"flex min-h-[3.25rem] flex-col items-center justify-center gap-0.5 px-1 py-1.5 text-[10px] font-semibold",
										isActive
											? "text-primary"
											: "text-muted-foreground hover:text-foreground",
									)}
								>
									<Icon
										className={cn(
											"size-5",
											isActive ? "text-primary" : "text-muted-foreground",
										)}
										aria-hidden
									/>
									{tab.label}
								</NavLink>
							</li>
						);
					})}
				</ul>
			</nav>
			{showSandbox ? <DemoNetworkBanner variant="mobile-soft" /> : null}
			{/* Home indicator / safe area under soft strip */}
			<div
				className="bg-card/95 pb-[env(safe-area-inset-bottom)]"
				aria-hidden
			/>
		</div>
	);
}
